import { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";

import {
  Home, LayoutDashboard, Building2, Receipt, Users, MoreHorizontal,
  X, Car, Repeat, Sparkles, Calculator, ScanLine, Archive,
  ShieldCheck, LogOut, ChevronRight, Sun, Moon, Wrench,
  FileText, Settings, Layers, HelpCircle, Compass, ClipboardCheck, Bell,
  ChevronDown, Check, Server, Clock, AlertTriangle, User
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useTheme } from "../lib/theme";
import { useEntity } from "../lib/entity";
import { useHelp } from "../lib/help";
import { toast } from "sonner";
import ErrorBoundary from "./ErrorBoundary";
import UserProfileModal from "./UserProfileModal";
import SignOutConfirmModal from "./SignOutConfirmModal";

const PRIMARY_NAV = [
  { to: "/", label: "Home", icon: Home, testId: "nav-dashboard" },
  { to: "/properties", label: "Properties", icon: Building2, testId: "nav-properties" },
  { to: "/transactions", label: "Transactions", icon: Receipt, testId: "nav-transactions" },
  { to: "/rent-roll", label: "Rent Roll", icon: Users, testId: "nav-rent-roll" },
];

const ROLE_BADGES = {
  owner: { label: "OWNER", className: "px-2.5 py-1 rounded-full text-[10px] font-extrabold tracking-wider uppercase bg-teal-500/15 text-teal-400 border border-teal-500/30 shadow-sm shrink-0" },
  editor: { label: "EDITOR", className: "px-2.5 py-1 rounded-full text-[10px] font-extrabold tracking-wider uppercase bg-blue-500/15 text-blue-400 border border-blue-500/30 shadow-sm shrink-0" },
  viewer: { label: "VIEWER", className: "px-2.5 py-1 rounded-full text-[10px] font-extrabold tracking-wider uppercase bg-slate-500/15 text-slate-400 border border-slate-500/30 shadow-sm shrink-0" },
};

const ROLE_INITIALS = {
  owner: { letter: "O", className: "w-6 h-6 rounded-full bg-teal-500/15 text-teal-400 border border-teal-500/30 font-black text-[11px] flex items-center justify-center shrink-0 shadow-sm" },
  editor: { letter: "E", className: "w-6 h-6 rounded-full bg-blue-500/15 text-blue-400 border border-blue-500/30 font-black text-[11px] flex items-center justify-center shrink-0 shadow-sm" },
  viewer: { letter: "V", className: "w-6 h-6 rounded-full bg-slate-500/15 text-slate-400 border border-slate-500/30 font-black text-[11px] flex items-center justify-center shrink-0 shadow-sm" },
};

export default function Layout({ children, title, subtitle, actions, hideNav = false }) {
  const { logout, user, can = {}, account } = useAuth() || {};
  const { isDark, toggleTheme } = useTheme();
  const { entities, activeEntityId, switchEntity } = useEntity();
  const { openHelp, startTour } = useHelp();
  const [moreOpen, setMoreOpen] = useState(false);
  const [mobileEntitySheetOpen, setMobileEntitySheetOpen] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const companyName = account?.name || "Rental Tracker";
  const logoInitials = (() => {
    if (!companyName) return "RT";
    const parts = companyName.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return companyName.slice(0, 2).toUpperCase();
  })();

  const [signOutModalOpen, setSignOutModalOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  const handleLogout = () => {
    setMoreOpen(false);
    setSignOutModalOpen(true);
  };

  const handleConfirmLogout = async () => {
    try {
      setSigningOut(true);
      await logout();
      toast.success("Signed out successfully");
      setSignOutModalOpen(false);
      navigate("/");
    } catch (err) {
      toast.error("Failed to sign out. Please try again.");
    } finally {
      setSigningOut(false);
    }
  };

  const roleBadge = user && (ROLE_BADGES[user.role] || ROLE_BADGES.viewer);
  const roleInitialBadge = user && (ROLE_INITIALS[user.role] || ROLE_INITIALS.viewer);
  const activeEntityObj = entities.find((e) => String(e.id) === String(activeEntityId));
  const activeEntityName = activeEntityObj ? activeEntityObj.name : "All Entities";

  const goTo = (path) => {
    setMoreOpen(false);
    navigate(path);
  };

  const isMoreActive = [
    "/settings", "/mileage", "/recurring", "/cost-seg", "/strategy", "/scan",
    "/maintenance", "/documents", "/admin", "/improvements", "/cpa-review"
  ].some((path) => location.pathname.startsWith(path));

  useEffect(() => {
    if (moreOpen || mobileEntitySheetOpen || profileModalOpen) {
      document.body.classList.add("has-modal-open");
    } else {
      document.body.classList.remove("has-modal-open");
    }
  }, [moreOpen, mobileEntitySheetOpen, profileModalOpen]);

  const [supportData, setSupportData] = useState(null);
  const [timeLeft, setTimeLeft] = useState("");
  const [exitingSupport, setExitingSupport] = useState(false);

  // Countdown timer for active support mode
  useEffect(() => {
    if (!user?.is_support_session || !user?.support_expires_at) return;

    const updateTimer = () => {
      const now = new Date();
      const exp = new Date(user.support_expires_at);
      const diff = Math.max(0, Math.floor((exp - now) / 1000));
      if (diff <= 0) {
        setTimeLeft("Expired");
        return;
      }
      const hours = Math.floor(diff / 3600);
      const minutes = Math.floor((diff % 3600) / 60);
      const seconds = diff % 60;
      if (hours > 0) {
        setTimeLeft(`${hours}h ${String(minutes).padStart(2, "0")}m ${String(seconds).padStart(2, "0")}s`);
      } else {
        setTimeLeft(`${String(minutes).padStart(2, "0")}m ${String(seconds).padStart(2, "0")}s`);
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [user?.is_support_session, user?.support_expires_at]);

  // Polling for owner pending support access requests
  useEffect(() => {
    if (!user || user.is_support_session) return;
    let isMounted = true;
    const fetchSupportState = async () => {
      try {
        const res = await api.get("/support-access/current");
        if (isMounted && res.data) {
          setSupportData(res.data);
        }
      } catch (e) {
        // ignore
      }
    };
    fetchSupportState();
    const timer = setInterval(fetchSupportState, 30000);
    return () => {
      isMounted = false;
      clearInterval(timer);
    };
  }, [user]);

  const handleExitSupportSession = async () => {
    setExitingSupport(true);
    try {
      await api.post(`/super-admin/support-requests/${user.support_request_id}/exit`);
      toast.success("Support session closed");
      window.location.href = "/super-admin";
    } catch (err) {
      window.location.href = "/super-admin";
    } finally {
      setExitingSupport(false);
    }
  };

  const handleApproveSupport = async (reqId) => {
    try {
      const res = await api.post(`/support-access/requests/${reqId}/approve`);
      if (res.data?.ok) {
        toast.success("Support access approved for Super Admin");
        setSupportData((prev) => ({ ...prev, pending_request: null }));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to approve access");
    }
  };

  const handleRejectSupport = async (reqId) => {
    try {
      const res = await api.post(`/support-access/requests/${reqId}/reject`);
      if (res.data?.ok) {
        toast.info("Support access declined");
        setSupportData((prev) => ({ ...prev, pending_request: null }));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to decline request");
    }
  };

  return (
    <div className="min-h-screen pb-[calc(env(safe-area-inset-bottom,0px)+96px)] md:pb-10 transition-colors duration-200" data-testid="app-shell">
      {/* 1. Super Admin Active Support Mode Banner */}
      {user?.is_support_session && (
        <div className="bg-gradient-to-r from-amber-600 via-amber-500 to-amber-600 text-slate-950 px-4 py-2 text-xs shadow-lg flex flex-wrap items-center justify-between gap-3 sticky top-0 z-50 border-b border-amber-400">
          <div className="flex items-center gap-2">
            <span className="flex h-2 w-2 relative">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-slate-950 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-slate-950"></span>
            </span>
            <span className="uppercase tracking-wider font-black text-[11px] flex items-center gap-1">
              <AlertTriangle size={14} className="inline -mt-0.5" />
              <span>Support Mode Active</span>
            </span>
            <span className="text-slate-900 font-semibold">•</span>
            <span className="bg-slate-950/20 px-2 py-0.5 rounded text-[11px] font-black uppercase tracking-wide">
              Role: {user.support_requested_role || user.role}
            </span>
            <span className="text-slate-900 font-semibold">•</span>
            <span className="flex items-center gap-1 text-[11px] font-bold">
              <Clock size={13} className="inline -mt-0.5" />
              <span>Expires in: <span className="font-mono font-black">{timeLeft || "Calculating..."}</span></span>
            </span>
          </div>
          <button
            onClick={handleExitSupportSession}
            disabled={exitingSupport}
            className="px-3 py-1 rounded-lg bg-slate-950 text-amber-300 hover:bg-slate-900 hover:text-white font-extrabold text-xs transition-colors flex items-center gap-1.5 shadow-sm"
          >
            <LogOut size={13} />
            <span>{exitingSupport ? "Exiting..." : "Exit Support Session"}</span>
          </button>
        </div>
      )}

      {/* 2. Owner Pending Support Request Alert Banner */}
      {supportData?.pending_request && (
        <div className="bg-gradient-to-r from-slate-900 via-[#1C3A5E] to-slate-900 border-b border-teal-500/40 text-white px-4 py-2.5 text-xs shadow-lg flex flex-wrap items-center justify-between gap-3 sticky top-0 z-40">
          <div className="flex items-center gap-2.5 flex-1 min-w-[280px]">
            <div className="w-8 h-8 rounded-xl bg-teal-500/20 border border-teal-500/40 text-teal-400 flex items-center justify-center shrink-0">
              <ShieldCheck size={18} />
            </div>
            <div className="text-xs">
              <div className="font-bold text-teal-300">
                Super Admin Support Request
              </div>
              <div className="text-slate-200 mt-0.5">
                Super Admin <strong className="text-white font-mono">@{supportData.pending_request.super_admin_username}</strong> requested{" "}
                <span className="px-1.5 py-0.5 rounded font-extrabold uppercase bg-teal-500/20 text-teal-300 border border-teal-500/30 text-[10px]">
                  {supportData.pending_request.requested_role}
                </span>{" "}
                access for <strong>{supportData.pending_request.duration_hours}h</strong>
              </div>
              <div className="text-slate-400 text-[11px] mt-0.5">
                Reason: <em>"{supportData.pending_request.reason}"</em>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => handleApproveSupport(supportData.pending_request.id)}
              className="px-3.5 py-1.5 rounded-xl bg-teal-500 hover:bg-teal-400 text-slate-950 font-bold text-xs flex items-center gap-1.5 transition-colors shadow-sm"
            >
              <Check size={14} />
              <span>Approve Access</span>
            </button>
            <button
              onClick={() => handleRejectSupport(supportData.pending_request.id)}
              className="px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white font-semibold text-xs flex items-center gap-1 transition-colors"
            >
              <X size={14} />
              <span>Decline</span>
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="sticky top-0 z-30 backdrop-blur-2xl bg-[var(--surface)]/95 border-b border-[var(--border)] pt-[env(safe-area-inset-top,0px)] transition-colors shadow-sm">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          {/* Desktop Single-Row Header (>= md) */}
          <div className="hidden md:flex items-center justify-between gap-4 h-16">
            {/* Left: Brand Logo + Company Name + Entity Dropdown */}
            <div className="flex items-center gap-3 shrink-0">
              <NavLink to="/" className="flex items-center gap-2.5 group shrink-0">
                <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#1C3A5E] to-[#0D9488] text-white flex items-center justify-center font-black text-xs shadow-md border border-teal-500/30 group-hover:scale-105 group-hover:shadow-teal-500/20 transition-all">
                  {logoInitials}
                </div>
                <span className="font-extrabold text-sm tracking-tight text-[var(--text)] group-hover:text-teal-400 transition-colors whitespace-nowrap">
                  {companyName}
                </span>
              </NavLink>

              <div className="h-4 w-[1px] bg-[var(--border)] mx-0.5" />

              {/* Glassmorphic Entity Switcher */}
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-xl border border-[var(--border)] bg-[var(--surface)]/80 hover:border-teal-500/40 transition-colors">
                <Layers size={13} className="text-teal-400 shrink-0" />
                <select
                  value={activeEntityId}
                  onChange={(e) => switchEntity(e.target.value)}
                  className="bg-transparent text-[var(--text)] font-semibold text-xs border-none outline-none cursor-pointer pr-1"
                  data-testid="entity-selector"
                  title="Filter by Legal Entity / LLC"
                >
                  <option value="all" className="bg-[#13131A] text-white">All Entities</option>
                  {entities.map((ent) => (
                    <option key={ent.id} value={ent.id} className="bg-[#13131A] text-white">
                      {ent.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Center: Navigation Tabs (Home, Properties, Transactions, Rent Roll, More) */}
            <nav className="flex items-center gap-1 bg-[var(--surface-hover)]/70 border border-[var(--border)]/80 p-1 rounded-2xl shadow-sm shrink-0" aria-label="Main Navigation">
              {PRIMARY_NAV.map((it) => (
                <NavLink
                  key={it.to}
                  to={it.to}
                  end={it.to === "/"}
                  data-testid={`desktop-${it.testId}`}
                  className={({ isActive }) =>
                    `px-3.5 py-1.5 rounded-xl text-xs font-semibold flex items-center gap-1.5 transition-all ${
                      isActive
                        ? "bg-[var(--surface)] text-teal-600 dark:text-teal-300 font-bold shadow-sm border border-[var(--border)]"
                        : "text-slate-600 dark:text-slate-400 hover:text-[var(--text)] hover:bg-[var(--surface-hover)]"
                    }`
                  }
                >
                  <it.icon size={15} />
                  <span>{it.label}</span>
                </NavLink>
              ))}

              <button
                type="button"
                onClick={() => setMoreOpen(true)}
                data-testid="desktop-nav-more"
                className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold flex items-center gap-1.5 transition-all ${
                  isMoreActive || moreOpen
                    ? "bg-[var(--surface)] text-teal-600 dark:text-teal-300 font-bold shadow-sm border border-[var(--border)]"
                    : "text-slate-600 dark:text-slate-400 hover:text-[var(--text)] hover:bg-[var(--surface-hover)]"
                }`}
              >
                <MoreHorizontal size={15} />
                <span>More</span>
              </button>
            </nav>

            {/* Right: Max 5 Utility Controls with 8px Gap */}
            <div className="flex items-center gap-2 shrink-0">
              {/* 1. User Profile Pill Button */}
              {user && (
                <button
                  type="button"
                  onClick={() => setProfileModalOpen(true)}
                  data-testid="header-user-profile-button"
                  className="flex items-center gap-2 px-2.5 py-1 rounded-xl border border-[var(--border)] bg-[var(--surface)] hover:bg-[var(--surface-hover)] hover:border-teal-500/40 text-[var(--text)] transition-all cursor-pointer shadow-sm group"
                  title="Account Profile & Settings"
                  aria-label="Account Profile"
                >
                  <div className="w-5 h-5 rounded-full bg-gradient-to-br from-teal-500 to-[#1C3A5E] text-white flex items-center justify-center text-[10px] font-black uppercase shadow-xs">
                    {(user.username || "U").charAt(0).toUpperCase()}
                  </div>
                  <span className="text-xs font-bold tracking-tight text-[var(--text)] group-hover:text-teal-400 transition-colors max-w-[120px] truncate">
                    {user.username}
                  </span>
                  {roleBadge && (
                    <span className={roleBadge.className} data-testid="header-role-badge">
                      {roleBadge.label}
                    </span>
                  )}
                </button>
              )}

              {/* 2. Help Icon (?) */}
              <button
                onClick={() => openHelp()}
                data-testid="header-help-button"
                className="w-9 h-9 rounded-xl border border-[var(--border)] bg-[var(--surface)] text-slate-600 dark:text-slate-400 hover:text-teal-400 hover:border-teal-500/40 flex items-center justify-center transition-all hover:scale-105"
                title="Landlord Help & IRS Guide"
                aria-label="Contextual Help"
              >
                <HelpCircle size={16} />
              </button>

              {/* 3. Dark/Light Mode Toggle */}
              <button
                onClick={toggleTheme}
                data-testid="theme-toggle"
                className="w-9 h-9 rounded-xl border border-[var(--border)] bg-[var(--surface)] text-slate-600 dark:text-slate-400 hover:text-[var(--text)] hover:border-teal-500/40 flex items-center justify-center transition-all hover:scale-105"
                title={isDark ? "Switch to Light mode" : "Switch to Dark mode"}
                aria-label="Toggle theme"
              >
                {isDark ? (
                  <Sun size={16} className="text-amber-400 transition-transform duration-300 rotate-0 hover:rotate-45" />
                ) : (
                  <Moon size={16} className="text-slate-600 transition-transform duration-300 -rotate-12 hover:rotate-0" />
                )}
              </button>

              {/* 4. Notifications Bell */}
              <button
                onClick={() => navigate("/cpa-review")}
                data-testid="header-notifications-button"
                className="w-9 h-9 relative rounded-xl border border-[var(--border)] bg-[var(--surface)] text-slate-600 dark:text-slate-400 hover:text-teal-400 hover:border-teal-500/40 flex items-center justify-center transition-all hover:scale-105"
                title="Notifications & Review Queue"
                aria-label="Notifications"
              >
                <Bell size={16} />
                <span className="absolute top-2 right-2 w-2 h-2 rounded-full bg-teal-400 animate-pulse shadow-sm shadow-teal-400/50" />
              </button>

              {/* 5. Logout */}
              <button
                onClick={handleLogout}
                data-testid="logout-button"
                className="w-9 h-9 rounded-xl border border-[var(--border)] bg-[var(--surface)] text-slate-500 hover:text-red-400 hover:border-red-500/30 flex items-center justify-center transition-all hover:scale-105"
                title="Sign out"
                aria-label="Sign out"
              >
                <LogOut size={16} />
              </button>
            </div>
          </div>

          {/* Optional Title & Action Strip for Sub-Pages */}
          {(title || actions) && (
            <div className="hidden md:flex items-start justify-between gap-4 py-3 border-t border-[var(--border)]/60">
              <div className="min-w-0">
                {title && <h1 className="text-xl font-black tracking-tight text-[var(--text)] truncate">{title}</h1>}
                {subtitle && <div className="text-xs text-[var(--muted)] truncate mt-0.5">{subtitle}</div>}
              </div>
              {actions && <div className="flex items-center gap-2 shrink-0 self-start">{actions}</div>}
            </div>
          )}

          {/* Mobile Header Layout (< md) - Bug 1: Max 56px main row, no truncation */}
          <div className="flex md:hidden flex-col">
            <div className="flex items-center justify-between gap-2 h-14">
              {/* Row 1 Left: Brand Logo + Company Name & Subtitle Entity Switcher */}
              <div className="flex items-center gap-2.5 min-w-0">
                <NavLink to="/" className="shrink-0" aria-label="Home">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#1C3A5E] to-[#0D9488] text-white flex items-center justify-center font-black text-xs shadow-sm">
                    {logoInitials}
                  </div>
                </NavLink>

                <div className="flex flex-col justify-center min-w-0">
                  <span
                    className="font-extrabold text-[13px] tracking-tight text-[var(--text)] leading-tight whitespace-nowrap"
                    title={companyName}
                  >
                    {companyName.length > 20
                      ? `${companyName.slice(0, 20)}…`
                      : companyName}
                  </span>
                  {/* Entity Switcher Subtitle Pill (tapping expands bottom sheet selector) */}
                  <button
                    type="button"
                    onClick={() => setMobileEntitySheetOpen(true)}
                    className="text-xs text-[var(--muted)] hover:text-teal-400 font-medium flex items-center gap-1 cursor-pointer transition-colors leading-tight mt-0.5 text-left"
                    data-testid="entity-selector-mobile"
                    title="Filter by Legal Entity / LLC"
                  >
                    <span className="truncate max-w-[130px]">{activeEntityName}</span>
                    <ChevronDown size={12} className="shrink-0 text-teal-400" />
                  </button>
                </div>
              </div>

              {/* Row 1 Right: Role Initial Circle + Max 3 Icons (Bell, Theme, Logout) */}
              <div className="flex items-center gap-1.5 shrink-0">
                {roleInitialBadge && (
                  <button
                    type="button"
                    onClick={() => setProfileModalOpen(true)}
                    className={`${roleInitialBadge.className} cursor-pointer hover:scale-105 active:scale-95 transition-all`}
                    title={`Profile: ${user?.username} (${user?.role || "viewer"})`}
                    data-testid="header-role-badge-mobile"
                    aria-label="View user profile"
                  >
                    {roleInitialBadge.letter}
                  </button>
                )}
                <button
                  onClick={() => navigate("/cpa-review")}
                  data-testid="notifications-mobile"
                  className="w-8 h-8 relative rounded-lg border border-[var(--border)] bg-[var(--surface)] text-[var(--text-secondary)] hover:text-teal-400 flex items-center justify-center transition-colors"
                  title="Notifications"
                  aria-label="Notifications"
                >
                  <Bell size={15} />
                  <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-teal-400 animate-pulse" />
                </button>
                <button
                  onClick={toggleTheme}
                  data-testid="theme-toggle-mobile"
                  className="w-8 h-8 rounded-lg border border-[var(--border)] bg-[var(--surface)] text-[var(--text-secondary)] hover:text-[var(--text)] flex items-center justify-center transition-colors"
                  title="Toggle theme"
                  aria-label="Toggle theme"
                >
                  {isDark ? <Sun size={15} className="text-amber-400" /> : <Moon size={15} className="text-slate-600" />}
                </button>
                <button
                  onClick={handleLogout}
                  data-testid="logout-button-mobile"
                  className="w-8 h-8 rounded-lg border border-[var(--border)] bg-[var(--surface)] text-slate-500 hover:text-red-400 flex items-center justify-center transition-colors"
                  title="Sign out"
                  aria-label="Sign out"
                >
                  <LogOut size={15} />
                </button>
              </div>
            </div>

            {/* Mobile Page Title & Actions (if applicable) - Bug 5: Top-aligned button regardless of subtitle wrapping */}
            {(title || actions) && (
              <div className="flex items-start justify-between gap-2 pt-2 pb-2.5 border-t border-[var(--border)]/40">
                <div className="min-w-0 flex-1">
                  {title && <h1 className="text-base font-black tracking-tight text-[var(--text)] truncate">{title}</h1>}
                  {subtitle && (
                    <div className="text-xs text-[var(--muted)] mt-0.5 leading-snug break-words">
                      {subtitle}
                    </div>
                  )}
                </div>
                {actions && <div className="shrink-0 self-start scale-95 origin-right">{actions}</div>}
              </div>
            )}
          </div>
        </div>

        {/* Gradient Accent Line Separator */}
        <div className="h-[1.5px] w-full bg-gradient-to-r from-[#1C3A5E]/40 via-teal-500/70 to-emerald-400/40 opacity-80" />
      </header>

      {/* Main Content Area (Max width 1400px centered) */}
      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8 page-enter">
        <ErrorBoundary>{children}</ErrorBoundary>
      </main>

      {/* Bottom Navigation (Mobile only <768px: 5 Tabs, 60px height, 44px min touch targets) */}
      <nav
        className={`bottom-nav md:hidden fixed bottom-0 left-0 right-0 z-40 bg-[var(--surface)]/95 backdrop-blur-xl border-t border-[var(--border)] h-[60px] flex items-center justify-around px-2 shadow-2xl transition-transform duration-200 ${
          moreOpen || hideNav ? "translate-y-full" : "translate-y-0"
        }`}
        data-testid="bottom-nav"
      >
        {PRIMARY_NAV.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.to === "/"}
            data-testid={it.testId}
            className={({ isActive }) =>
              `flex flex-col items-center justify-center flex-1 min-h-[44px] py-1 transition-colors ${
                isActive ? "text-teal-600 dark:text-teal-400 font-bold" : "text-slate-500 dark:text-slate-400 hover:text-[var(--text)]"
              }`
            }
          >
            <it.icon size={20} />
            <span className="text-[10px] tracking-tight mt-0.5">{it.label}</span>
          </NavLink>
        ))}

        {/* 5th Tab: More Drawer Trigger */}
        <button
          type="button"
          onClick={() => setMoreOpen(true)}
          data-testid="nav-more"
          className={`flex flex-col items-center justify-center flex-1 min-h-[44px] py-1 transition-colors ${
            isMoreActive || moreOpen ? "text-teal-600 dark:text-teal-400 font-bold" : "text-slate-500 dark:text-slate-400 hover:text-[var(--text)]"
          }`}
        >
          <MoreHorizontal size={20} />
          <span className="text-[10px] tracking-tight mt-0.5">More</span>
        </button>
      </nav>

      {/* Mobile Entity Switcher Bottom Sheet */}
      {mobileEntitySheetOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end justify-center"
          onClick={() => setMobileEntitySheetOpen(false)}
          data-testid="mobile-entity-sheet-backdrop"
        >
          <div
            className="w-full bg-[var(--surface)] border-t border-[var(--border)] rounded-t-3xl p-5 max-h-[80vh] overflow-y-auto animate-in slide-in-from-bottom duration-200"
            onClick={(e) => e.stopPropagation()}
            data-testid="mobile-entity-sheet"
          >
            <div className="flex items-center justify-between pb-3 border-b border-[var(--border)]">
              <div className="flex items-center gap-2">
                <Layers size={16} className="text-teal-400" />
                <h3 className="font-bold text-sm text-[var(--text)]">Select Entity</h3>
              </div>
              <button
                type="button"
                onClick={() => setMobileEntitySheetOpen(false)}
                className="p-1 rounded-lg text-[var(--muted)] hover:text-[var(--text)]"
              >
                <X size={16} />
              </button>
            </div>

            <div className="py-2 divide-y divide-[var(--border)]/50">
              <button
                type="button"
                onClick={() => {
                  switchEntity("all");
                  setMobileEntitySheetOpen(false);
                }}
                className={`w-full py-3 px-2 flex items-center justify-between text-sm transition-colors rounded-xl ${
                  activeEntityId === "all" ? "text-teal-400 font-bold bg-teal-500/10" : "text-[var(--text)] hover:bg-[var(--surface-hover)]"
                }`}
              >
                <span>All Entities</span>
                {activeEntityId === "all" && <Check size={16} className="text-teal-400" />}
              </button>
              {entities.map((ent) => (
                <button
                  key={ent.id}
                  type="button"
                  onClick={() => {
                    switchEntity(ent.id);
                    setMobileEntitySheetOpen(false);
                  }}
                  className={`w-full py-3 px-2 flex items-center justify-between text-sm transition-colors rounded-xl ${
                    String(activeEntityId) === String(ent.id) ? "text-teal-400 font-bold bg-teal-500/10" : "text-[var(--text)] hover:bg-[var(--surface-hover)]"
                  }`}
                >
                  <span className="truncate text-left">{ent.name}</span>
                  {String(activeEntityId) === String(ent.id) && <Check size={16} className="text-teal-400 shrink-0 ml-2" />}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* "More" Drawer / Modal */}
      {moreOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={() => setMoreOpen(false)}
          data-testid="more-drawer-backdrop"
        >
          <div
            className="w-full sm:max-w-xl bg-[var(--surface)] border border-[var(--border)] rounded-t-3xl sm:rounded-3xl shadow-2xl p-6 max-h-[85vh] overflow-y-auto space-y-6"
            onClick={(e) => e.stopPropagation()}
            data-testid="more-drawer"
          >
            {/* Drawer Header */}
            <div className="flex items-center justify-between border-b border-[var(--border)] pb-4">
              <button
                type="button"
                onClick={() => {
                  setMoreOpen(false);
                  setProfileModalOpen(true);
                }}
                className="flex items-center gap-3 text-left hover:opacity-85 transition-opacity cursor-pointer group"
                title="View & Edit Account Profile"
              >
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#1C3A5E] to-[#0D9488] text-white flex items-center justify-center font-black text-sm shadow-md group-hover:scale-105 transition-transform">
                  {user?.username ? user.username.slice(0, 2).toUpperCase() : "LP"}
                </div>
                <div>
                  <div className="font-bold text-base flex items-center gap-2">
                    <span className="group-hover:text-teal-400 transition-colors">{user?.username}</span>
                    {roleBadge && <span className={roleBadge.className}>{roleBadge.label}</span>}
                  </div>
                  <div className="text-xs text-[var(--muted)]">{user?.email || "Account Profile & Settings"}</div>
                </div>
              </button>
              <button
                onClick={() => setMoreOpen(false)}
                className="p-2 rounded-xl text-[var(--muted)] hover:bg-[var(--surface-hover)]"
                data-testid="close-more-drawer"
              >
                <X size={20} />
              </button>
            </div>

            {/* Enterprise & Property Operations */}
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-[var(--muted)] font-semibold mb-2 px-1">
                Operations & Maintenance
              </div>
              <div className="grid grid-cols-1 gap-1.5">
                <DrawerItem
                  icon={Wrench}
                  title="Maintenance Requests"
                  desc="Track repair jobs, contractor work, photos & invoices"
                  onClick={() => goTo("/maintenance")}
                  testId="drawer-maintenance"
                />
                <DrawerItem
                  icon={FileText}
                  title="Document Vault"
                  desc="Store leases, insurance policies, deeds & tax forms securely"
                  onClick={() => goTo("/documents")}
                  testId="drawer-documents"
                />
                <DrawerItem
                  icon={Repeat}
                  title="Recurring Schedules"
                  desc="Automated monthly rent, mortgage, and utility postings"
                  onClick={() => goTo("/recurring")}
                  testId="drawer-recurring"
                />
                <DrawerItem
                  icon={ScanLine}
                  title="AI Receipt Scanner"
                  desc="Gemini OCR receipt extraction & itemized deductions"
                  onClick={() => goTo("/scan")}
                  testId="drawer-scan"
                />
              </div>
            </div>

            {/* Tax & Financial Operations */}
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-[var(--muted)] font-semibold mb-2 px-1">
                Tax & Accounting
              </div>
              <div className="grid grid-cols-1 gap-1.5">
                <DrawerItem
                  icon={Archive}
                  title="Tax Center & Accountant Package"
                  desc="Schedule E, 1-click ZIP export & entity-filtered books"
                  onClick={() => goTo("/settings")}
                  testId="drawer-tax-package"
                />
                <DrawerItem
                  icon={ShieldCheck}
                  title="Capital Improvements"
                  desc="Depreciable capital assets, recovery periods & Form 4562 prep"
                  onClick={() => goTo("/improvements")}
                  testId="drawer-improvements"
                />
                <DrawerItem
                  icon={ClipboardCheck}
                  title="CPA Review Queue"
                  desc="Flagged transactions, deductibility resolution & CPA notes"
                  onClick={() => goTo("/cpa-review")}
                  testId="drawer-cpa-review"
                />
                <DrawerItem
                  icon={Sparkles}
                  title="Cost Segregation & Depreciation"
                  desc="IRS asset class breakdown & bonus depreciation schedules"
                  onClick={() => goTo("/cost-seg")}
                  testId="drawer-cost-seg"
                />
                <DrawerItem
                  icon={Car}
                  title="Mileage Tracker"
                  desc="IRS standard rate ($0.70/mi) business travel deductions"
                  onClick={() => goTo("/mileage")}
                  testId="drawer-mileage"
                />
                <DrawerItem
                  icon={Calculator}
                  title="Strategy Calculator"
                  desc="Compare Long-Term vs Short-Term rental returns"
                  onClick={() => goTo("/strategy")}
                  testId="drawer-strategy"
                />
              </div>
            </div>

            {/* Settings & Admin */}
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-[var(--muted)] font-semibold mb-2 px-1">
                System & Configuration
              </div>
              <div className="grid grid-cols-1 gap-1.5">
                <DrawerItem
                  icon={User}
                  title="My Account Profile"
                  desc="View credentials, change password, manage active sessions & roles"
                  onClick={() => {
                    setMoreOpen(false);
                    setProfileModalOpen(true);
                  }}
                  testId="drawer-profile"
                />
                <DrawerItem
                  icon={Settings}
                  title="Settings & LLC Entities"
                  desc="Manage LLCs, email notification templates & app settings"
                  onClick={() => goTo("/settings")}
                  testId="drawer-settings"
                />
                {can?.admin && (
                  <DrawerItem
                    icon={ShieldCheck}
                    title="Admin Portal"
                    desc="User accounts, activity logs, sessions & system security"
                    onClick={() => goTo("/admin")}
                    testId="drawer-admin"
                  />
                )}
                <DrawerItem
                  icon={Server}
                  title="Super Admin Mission Control"
                  desc="Platform dashboard, customer companies, SMTP, logs & health"
                  onClick={() => goTo("/super-admin")}
                  testId="drawer-super-admin"
                />
                <DrawerItem
                  icon={HelpCircle}
                  title="Help Center & IRS Guide"
                  desc="Plain English explanations, step-by-step guides & IRS tax rules"
                  onClick={() => {
                    setMoreOpen(false);
                    openHelp();
                  }}
                  testId="drawer-help"
                />
                <DrawerItem
                  icon={Compass}
                  title="Take App Tour"
                  desc="Interactive 3-step walkthrough of Dashboard, Books & AI OCR"
                  onClick={() => {
                    setMoreOpen(false);
                    startTour();
                  }}
                  testId="drawer-tour"
                />
                <DrawerItem
                  icon={LogOut}
                  title="Sign Out"
                  desc="End active session"
                  danger
                  onClick={handleLogout}
                  testId="drawer-logout"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* User Profile & Account Settings Modal */}
      <UserProfileModal
        open={profileModalOpen}
        onClose={() => setProfileModalOpen(false)}
      />

      {/* Sign Out Confirmation Dialog */}
      <SignOutConfirmModal
        open={signOutModalOpen}
        onClose={() => !signingOut && setSignOutModalOpen(false)}
        onConfirm={handleConfirmLogout}
        userName={user?.username}
        accountName={companyName}
        role={user?.role}
        signingOut={signingOut}
      />
    </div>
  );
}

function DrawerItem({ icon: Icon, title, desc, onClick, danger, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`w-full flex items-center justify-between p-3 rounded-2xl text-left transition-all ${
        danger
          ? "hover:bg-red-500/10 text-red-500"
          : "hover:bg-[var(--surface-hover)] text-[var(--text)]"
      }`}
    >
      <div className="flex items-center gap-3.5 min-w-0">
        <div
          className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
            danger
              ? "bg-red-500/15 text-red-500"
              : "bg-teal-500/15 text-teal-400"
          }`}
        >
          <Icon size={19} />
        </div>
        <div className="min-w-0">
          <div className="font-semibold text-sm truncate">{title}</div>
          <div className="text-xs text-[var(--muted)] truncate">{desc}</div>
        </div>
      </div>
      <ChevronRight size={16} className="text-[var(--muted)] shrink-0 ml-2" />
    </button>
  );
}
